import type {
  Logger,
  LoggerErrorProperties,
  LoggerProperties,
  LoggerPropertiesUpdate,
} from "@inference-net/cf-compatible-models";

import { LogLevel } from "@kuzco/models";

import { stringToLogLevel } from "./getLogLevel";

const DEFAULT_LOG_LEVEL = "info";

const LOG_LEVEL =
  typeof process !== "undefined"
    ? (process.env.LOG_LEVEL ?? DEFAULT_LOG_LEVEL)
    : DEFAULT_LOG_LEVEL;

type LoggerParams = {
  name: string;
  defaultProperties?: LoggerProperties;
  level?: LogLevel;
  hideDetails?: boolean;
};

export class BrowserSafeConsoleLogger implements Logger {
  public name: string;
  public level: LogLevel;
  private hideDetails: boolean;
  private defaultProperties: Record<string, unknown>;

  constructor(params: LoggerParams) {
    this.name = params.name;
    this.level = params.level ?? stringToLogLevel(LOG_LEVEL);
    this.hideDetails = params.hideDetails ?? false;
    this.defaultProperties = {
      ...params.defaultProperties,
      name: params.name,
    };
  }

  public setProperties(properties: LoggerPropertiesUpdate): void {
    for (const [key, value] of Object.entries(properties)) {
      if (value === undefined) {
        delete this.defaultProperties[key];
        continue;
      }

      this.defaultProperties[key] = value;
    }
  }

  public setLogLevel(level: LogLevel): void {
    this.level = level;
  }

  /** Keys to promote into the log prefix for at-a-glance context. */
  private static readonly CONTEXT_KEYS = [
    "instanceId",
    "shardIndex",
    "generationId",
  ] as const;

  private shouldLog(messageLevel: LogLevel): boolean {
    return messageLevel >= this.level;
  }

  private print({
    message,
    level,
    error,
    properties,
  }: {
    message: string;
    level: LogLevel;
    error?: unknown;
    properties?: LoggerProperties;
  }) {
    if (!this.shouldLog(level)) {
      return;
    }

    const mergedProperties = {
      ...this.defaultProperties,
      ...(properties ?? {}),
    };

    // Build header with context keys from merged (default + per-call) properties
    const headerParts: string[] = [this.name];
    for (const key of BrowserSafeConsoleLogger.CONTEXT_KEYS) {
      const value = mergedProperties[key];
      if (value != null) {
        headerParts.push(`${key}=${String(value)}`);
      }
    }

    const toLog: unknown[] = [
      `[${headerParts.join(" ")}]:`,
      new Date().toISOString(),
      level,
      message,
    ];

    if (error) {
      toLog.push(error);
    }

    if (!this.hideDetails && Object.keys(mergedProperties).length > 0) {
      toLog.push(mergedProperties);
    }

    console.log(...toLog);
  }

  debug(message: string, properties?: LoggerProperties): void {
    this.print({ message, level: LogLevel.Debug, properties });
  }

  info(message: string, properties?: LoggerProperties): void {
    this.print({ message, level: LogLevel.Info, properties });
  }

  warn(message: string, properties?: LoggerProperties): void {
    this.print({ message, level: LogLevel.Warn, properties });
  }

  error(message: string, params?: LoggerErrorProperties): void {
    const { error, ...properties } = params ?? {};
    this.print({ message, level: LogLevel.Error, error, properties });
  }

  fatal(message: string, params?: LoggerErrorProperties): void {
    const { error, ...properties } = params ?? {};
    this.print({ message, level: LogLevel.Fatal, error, properties });
  }

  public child(name: string, properties?: LoggerProperties): Logger {
    return new BrowserSafeConsoleLogger({
      name: `${this.name}|${name}`,
      level: this.level,
      hideDetails: this.hideDetails,
      defaultProperties: {
        ...this.defaultProperties,
        ...properties,
      },
    });
  }

  public async flush(): Promise<void> {
    // No-op
  }
}
